/**
 * Risk Item Card Component
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { InventoryRiskItem, RiskSeverity } from "../types";
import { parseInventoryName } from "../inventoryName";

interface RiskItemCardProps {
  item: InventoryRiskItem;
  isIgnored: boolean;
  isIgnoreLoading: boolean;
  onViewInTable: (groupingKey: string) => void;
  onLeadTimeChange: (inventoryId: string, newLeadTimeMonths: number) => void;
  onToggleIgnore: (inventoryId: string, country: string) => void;
}

const SEVERITY_CONFIG: Record<RiskSeverity, { bg: string; text: string; label: string; tooltip: string }> = {
  CRITICAL: {
    bg: "#ffebee",
    text: "#c62828",
    label: "CRITICAL",
    tooltip: "Stock-out imminent (less than 2 months)"
  },
  "AT-RISK": {
    bg: "#fff3e0",
    text: "#e65100",
    label: "AT-RISK",
    tooltip: "Stock-out approaching (2-3.5 months)"
  },
  WATCH: {
    bg: "#fffde7",
    text: "#f9a825",
    label: "WATCH",
    tooltip: "Stock-out possible (2-3 months)"
  },
  OK: {
    bg: "#e8f5e9",
    text: "#2e7d32",
    label: "OK",
    tooltip: "No immediate concern (more than 3 months)"
  },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "\u2014";
  return moment(dateStr).format("MMM D, YYYY");
}

function formatDaysUntil(days: number | null): string {
  if (days === null) return "";
  if (days < 0) return `(${Math.abs(days)} days ago)`;
  if (days === 0) return "(today)";
  if (days === 1) return "(1 day)";
  return `(${days} days)`;
}

export default function RiskItemCard({
  item,
  isIgnored,
  isIgnoreLoading,
  onViewInTable,
  onLeadTimeChange,
  onToggleIgnore,
}: RiskItemCardProps): JSX.Element {
  const [isEditingLeadTime, setIsEditingLeadTime] = useState(false);
  // The chip displays the final lead time (base + country adjustment), but a
  // saved override is a BASE value the adjustment gets added onto — so the
  // editor works in base months.
  const baseLeadTimeMonths = item.leadTimeMonths - (item.leadTimeCountryAdjustmentMonths ?? 0);
  const [leadTimeInput, setLeadTimeInput] = useState(String(baseLeadTimeMonths));

  const severityConfig = SEVERITY_CONFIG[item.severity];
  const { name, size } = parseInventoryName(item.inventory_name);
  const isOverdue = item.daysUntilOrderNeeded !== null && item.daysUntilOrderNeeded < 0;
  const isStockedOut = item.currentInventory <= 0;
  // A stocked-out item's trailing rate is 0 by definition, so show its
  // availability-adjusted demand rate instead. One value drives both the label
  // and the number, so they can never claim different things.
  const inStockRate = isStockedOut ? item.demandDailyRate : undefined;

  const handleLeadTimeBlur = () => {
    setIsEditingLeadTime(false);
    const newValue = parseFloat(leadTimeInput);
    if (!isNaN(newValue) && newValue > 0 && newValue !== baseLeadTimeMonths) {
      onLeadTimeChange(item.inventory_id, newValue);
    } else {
      setLeadTimeInput(String(baseLeadTimeMonths));
    }
  };

  const handleLeadTimeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLeadTimeBlur();
    } else if (e.key === "Escape") {
      setLeadTimeInput(String(baseLeadTimeMonths));
      setIsEditingLeadTime(false);
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: isIgnored ? "#F8FAFC" : severityConfig.bg,
        border: `1px solid ${isIgnored ? "#E2E8F0" : `${severityConfig.text}20`}`,
        mb: 1,
        opacity: isIgnored ? 0.5 : 1,
        transition: "opacity 0.2s, background-color 0.2s",
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        {/* Header: severity chip on its own row, then the name in a reserved
            two-line space (so cards stay the same height whether the name
            takes one line or two), then the size on its own labeled line. */}
        <Box sx={{ mb: 1 }}>
          <Tooltip title={isStockedOut ? "Item is currently out of stock" : severityConfig.tooltip}>
            <Chip
              label={isStockedOut ? "STOCKED OUT" : severityConfig.label}
              size="small"
              sx={{
                backgroundColor: isIgnored ? "#9e9e9e" : severityConfig.text,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          </Tooltip>
          <Tooltip title={item.inventory_name || ""}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.35,
                mt: 0.75,
                minHeight: "2.7em",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {name || "Unnamed"}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ display: "block" }}>
            <Box component="span" sx={{ opacity: 0.7 }}>
              Size:{" "}
            </Box>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {size ?? "\u2014"}
            </Box>
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
            {item.inventory_id} {"\u2022"} {item.country}
          </Typography>
        </Box>

        {/* Metrics row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            fontSize: "0.8rem",
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
              Stock-out
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatDate(item.projectedStockOutDate)}{" "}
              <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
                {formatDaysUntil(item.daysUntilStockOut)}
              </Typography>
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
              Order by
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: isOverdue ? "#c62828" : "inherit" }}
            >
              {formatDate(item.recommendedOrderDate)}
              {isOverdue && (
                <Typography component="span" variant="caption" sx={{ color: "#c62828", ml: 0.5 }}>
                  OVERDUE
                </Typography>
              )}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
              Current
            </Typography>
            <Typography variant="body2">
              {item.currentInventory <= 0 ? "0" : Math.round(item.currentInventory).toLocaleString()} units
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
              {inStockRate !== undefined ? "Rate when in stock" : "Daily rate"}
            </Typography>
            <Typography variant="body2">
              {(inStockRate ?? item.dailyRunRate).toFixed(1)} units/day
            </Typography>
          </Box>
        </Box>

        {/* Footer row: Lead time + action buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${isIgnored ? "#e0e0e0" : `${severityConfig.text}20`}`,
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Lead time:
          </Typography>
          {isEditingLeadTime ? (
            <TextField
              size="small"
              type="number"
              value={leadTimeInput}
              onChange={(e) => setLeadTimeInput(e.target.value)}
              onBlur={handleLeadTimeBlur}
              onKeyDown={handleLeadTimeKeyDown}
              autoFocus
              inputProps={{ min: 0.5, step: 0.5, style: { padding: "2px 8px", width: 50 } }}
              sx={{ "& .MuiOutlinedInput-root": { height: 24 } }}
            />
          ) : (
            <Tooltip
              title={[
                item.leadTimeSource === "inventory"
                  ? "SKU override — click to edit"
                  : item.leadTimeSource === "manufacturer"
                    ? "Manufacturer default — click to set a SKU override"
                    : "Global default — click to set a SKU override",
                item.leadTimeCountryAdjustmentMonths
                  ? `Includes ${item.leadTimeCountryAdjustmentMonths > 0 ? "+" : ""}${item.leadTimeCountryAdjustmentMonths} mo ${item.country} adjustment`
                  : "",
              ].filter(Boolean).join(". ")}
            >
              <Chip
                label={`${item.leadTimeMonths} ${item.leadTimeMonths === 1 ? 'month' : 'months'}`}
                size="small"
                onClick={() => setIsEditingLeadTime(true)}
                sx={{
                  cursor: "pointer",
                  height: 22,
                  fontSize: "0.75rem",
                  "&:hover": { backgroundColor: `${severityConfig.text}20` },
                }}
              />
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="text"
            disabled={isIgnoreLoading}
            onClick={() => onToggleIgnore(item.inventory_id, item.country)}
            sx={{
              minWidth: 0,
              px: 1,
              py: 0.25,
              fontSize: "0.7rem",
              textTransform: "none",
              color: isIgnored ? "#2e7d32" : "#757575",
            }}
          >
            {isIgnoreLoading ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : null}
            {isIgnored ? "Unignore" : "Ignore"}
          </Button>

          <Button
            size="small"
            variant="text"
            onClick={() => onViewInTable(item.groupingKey)}
            sx={{
              minWidth: 0,
              px: 1,
              py: 0.25,
              fontSize: "0.7rem",
              textTransform: "none",
              color: isIgnored ? "#9e9e9e" : severityConfig.text,
            }}
          >
            View
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
